// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';

const FlyingBirdGame = () => {
  const canvasRef = useRef(null);
  // canvasHeight state removed (unused) — canvas is sized directly
  // Reserve this height (px) for Google Ads (banner). Change to 90/100 as needed.
  const AD_HEIGHT = 90;
  // (Donation widget is provided by BuyMeACoffee script in public/index.html)
  const [gameState, setGameState] = useState('menu'); // menu, playing, gameover
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  
  const gameRef = useRef({
    bird: { x: 80, y: 250, velocity: 0, radius: 15 },
    pipes: [],
    score: 0,
    gravity: 0.5,
    jumpStrength: -9,
    pipeWidth: 60,
    pipeGap: 180,
    pipeSpeed: 3,
    frameCount: 0,
    gameLoop: null,
    restartButton: { x: 0, y: 0, width: 0, height: 0 }
  });

  // Load high score from storage on mount
  useEffect(() => {
    // Load high score from localStorage on mount
    const stored = localStorage.getItem('flying_high_score');
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!Number.isNaN(parsed)) setHighScore(parsed);
    }
  }, []);

  // Save high score to storage
  const saveHighScore = (newScore: any) => {
    try {
      localStorage.setItem('flying_high_score', newScore.toString());
    } catch (error) {
      console.error('Failed to save high score:', error);
    }
  };

  const endGame = useCallback(() => {
    const game = gameRef.current;
    setGameState('gameover');
    
    if (game.score > highScore) {
      setHighScore(game.score);
      saveHighScore(game.score);
    }
  }, [highScore]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Resize helper: set canvas pixel size to available viewport minus ad area
    const resizeCanvas = () => {
      const w = window.innerWidth;
      const h = Math.max(200, window.innerHeight - AD_HEIGHT);
      canvas.width = w;
      canvas.height = h;
    };

    resizeCanvas();

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const game = gameRef.current;

    const drawBird = () => {
      ctx.fillStyle = '#FFD700';
      ctx.beginPath();
      ctx.arc(game.bird.x, game.bird.y, game.bird.radius, 0, Math.PI * 2);
      ctx.fill();
      
      // Eye
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(game.bird.x + 5, game.bird.y - 3, 3, 0, Math.PI * 2);
      ctx.fill();
      
      // Beak
      ctx.fillStyle = '#FF6347';
      ctx.beginPath();
      ctx.moveTo(game.bird.x + 12, game.bird.y);
      ctx.lineTo(game.bird.x + 20, game.bird.y);
      ctx.lineTo(game.bird.x + 12, game.bird.y + 5);
      ctx.fill();
    };

    const drawPipe = (pipe: any) => {
      ctx.fillStyle = '#2ECC40';
      ctx.fillRect(pipe.x, 0, game.pipeWidth, pipe.top);
      ctx.fillRect(pipe.x, pipe.bottom, game.pipeWidth, canvas.height - pipe.bottom);
      
      // Pipe borders
      ctx.strokeStyle = '#1a7a26';
      ctx.lineWidth = 3;
      ctx.strokeRect(pipe.x, 0, game.pipeWidth, pipe.top);
      ctx.strokeRect(pipe.x, pipe.bottom, game.pipeWidth, canvas.height - pipe.bottom);
    };

    const drawButton = (x: any, y: any, width: any, height: any, text: any) => {
      // Button shadow
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(x + 2, y + 2, width, height);
      
      // Button background
      ctx.fillStyle = '#22C55E';
      ctx.fillRect(x, y, width, height);
      
      // Button border
      ctx.strokeStyle = '#16A34A';
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, width, height);
      
      // Button text
      ctx.fillStyle = '#FFF';
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x + width / 2, y + height / 2);
    };

    const checkCollision = () => {
      const bird = game.bird;
      
      // Check ground and ceiling
      if (bird.y + bird.radius > canvas.height || bird.y - bird.radius < 0) {
        return true;
      }

      // Check pipes
      for (let pipe of game.pipes) {
        if (bird.x + bird.radius > pipe.x && bird.x - bird.radius < pipe.x + game.pipeWidth) {
          if (bird.y - bird.radius < pipe.top || bird.y + bird.radius > pipe.bottom) {
            return true;
          }
        }
      }
      return false;
    };

    const updateGame = () => {
      if (gameState !== 'playing') return;

      game.frameCount++;
      
      // Update bird
      game.bird.velocity += game.gravity;
      game.bird.y += game.bird.velocity;

      // Add new pipes
      if (game.frameCount % 90 === 0) {
        const minTop = 50;
        const maxTop = canvas.height - game.pipeGap - 50;
        const top = Math.random() * (maxTop - minTop) + minTop;
        game.pipes.push({
          x: canvas.width,
          top: top,
          bottom: top + game.pipeGap,
          passed: false
        });
      }

      // Update pipes
      game.pipes = game.pipes.filter((pipe) => {
        pipe.x -= game.pipeSpeed;
        
        // Score when passing pipe
        if (!pipe.passed && pipe.x + game.pipeWidth < game.bird.x) {
          pipe.passed = true;
          game.score++;
          setScore(game.score);
        }
        
        return pipe.x + game.pipeWidth > 0;
      });

      // Check collision
        if (checkCollision()) {
          endGame();
        }
    };

    const draw = () => {
      // Clear canvas
      ctx.fillStyle = '#87CEEB';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw ground
      ctx.fillStyle = '#DEB887';
      ctx.fillRect(0, canvas.height - 30, canvas.width, 30);

      if (gameState === 'menu') {
        ctx.fillStyle = '#000';
        ctx.font = 'bold 40px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Flying Bird', canvas.width / 2, 150);
        
        ctx.font = '24px Arial';
        ctx.fillText('Tap or Click to Start', canvas.width / 2, 250);
        
        ctx.font = '20px Arial';
        ctx.fillText(`High Score: ${highScore}`, canvas.width / 2, 320);
        
        // Draw bird in menu
        game.bird.y = 250;
        game.bird.velocity = 0;
        drawBird();
      } else if (gameState === 'playing') {
        // Draw pipes
        game.pipes.forEach(drawPipe);
        
        // Draw bird
        drawBird();
        
        // Draw score
        ctx.fillStyle = '#000';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(game.score, canvas.width / 2, 50);
      } else if (gameState === 'gameover') {
        // Draw pipes
        game.pipes.forEach(drawPipe);
        
        // Draw bird
        drawBird();
        
        // Game over screen
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(canvas.width / 2 - 150, 150, 300, 250);
        
        ctx.fillStyle = '#FFF';
        ctx.font = 'bold 36px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Game Over', canvas.width / 2, 200);
        
        ctx.font = '24px Arial';
        ctx.fillText(`Score: ${game.score}`, canvas.width / 2, 250);
        ctx.fillText(`High Score: ${highScore}`, canvas.width / 2, 290);
        
        // Draw restart button
        const buttonWidth = 180;
        const buttonHeight = 50;
        const buttonX = canvas.width / 2 - buttonWidth / 2;
        const buttonY = 330;
        
        game.restartButton = { x: buttonX, y: buttonY, width: buttonWidth, height: buttonHeight };
        drawButton(buttonX, buttonY, buttonWidth, buttonHeight, 'Click to Restart');
      }
    };

    const gameLoop = () => {
      updateGame();
      draw();
      game.gameLoop = requestAnimationFrame(gameLoop);
    };

    window.addEventListener('resize', resizeCanvas);

    gameLoop();

    return () => {
      if (game.gameLoop) {
        cancelAnimationFrame(game.gameLoop);
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [gameState, highScore, endGame]);

  const startGame = () => {
    const game = gameRef.current;
    game.bird = { x: 80, y: 250, velocity: 0, radius: 15 };
    game.pipes = [];
    game.score = 0;
    game.frameCount = 0;
    setScore(0);
    setGameState('playing');
  };


  const handleCanvasClick = (e: any) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const game = gameRef.current;

    if (gameState === 'menu') {
      startGame();
    } else if (gameState === 'playing') {
      game.bird.velocity = game.jumpStrength;
    } else if (gameState === 'gameover') {
      // Check if click is within restart button
      const btn = game.restartButton;
      if (x >= btn.x && x <= btn.x + btn.width && y >= btn.y && y <= btn.y + btn.height) {
        startGame();
      }
    }
  };

  const handleKeyPress = useCallback((e: any) => {
    if (e.code === 'Space' || e.code === 'ArrowUp') {
      e.preventDefault();
      if (gameState === 'playing') {
        gameRef.current.bird.velocity = gameRef.current.jumpStrength;
      }
    }
  }, [gameState]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  // no-op: donation widget handled by external BuyMeACoffee script

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', margin: 0, padding: 0, overflow: 'hidden' }}>
      {/* Instructions banner */}
      <div style={{ height: AD_HEIGHT, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderBottom: '2px solid rgba(255,255,255,0.08)', flexShrink: 0, padding: '0 20px' }}>
        <div style={{ width: '100%', maxWidth: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20 }}>
              🐦
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Flying Bird</div>
              <div style={{ fontSize: 12, marginTop: 4, color: 'rgba(224,231,255,0.9)' }}>High Score: {highScore}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 24, alignItems: 'center', color: 'rgba(224,231,255,0.95)' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>How to play</div>
              <div style={{ fontSize: 12, marginTop: 6, maxWidth: 420 }}>
                <div>• Press anywhere to start</div>
                <div>• Click <strong>SPACE</strong> button/ Tap to move the bird up</div>
                <div>• Avoid pipes and the ground</div>
                <div>• Score increases when you pass pipes</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Game canvas — fills remaining space */}
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        style={{ flex: 1, display: 'block', backgroundColor: '#87CEEB' }}
        className="cursor-pointer"
      />

      {/* Overlay info (score, instructions) — only show in menu/gameover */}
      {gameState !== 'playing' && (
        <div style={{ position: 'fixed', bottom: 20, left: 20, backgroundColor: 'rgba(255, 255, 255, 0.9)', padding: '12px 20px', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontSize: '14px', color: '#333', zIndex: 1000 }}>
          Score: <strong>{score}</strong>
        </div>
      )}
    </div>
  );
};

export default FlyingBirdGame;